/**
 * useInactivityLogout.js  —  VATICA AI (AVALITH IT PTY LTD)
 * ─────────────────────────────────────────────────────────────────────────────
 * Ends a session after 20 minutes of LIVE inactivity (tab stays open, no
 * mousedown/keydown/touchstart/click for 20 minutes straight).
 *
 * WHY CLOSED TABS / SHUT-DOWN LAPTOPS ALREADY FORCE A FRESH LOGIN
 * ────────────────────────────────────────────────────────────────
 * This hook used to also run a "is the stored timestamp already stale?"
 * check on every mount and call onLogout() immediately if so — the idea
 * being that a closed tab or shut-down laptop would leave a stale stamp
 * for the next visit to catch. That check has been removed: it was firing
 * on ordinary fresh logins, not just stale revisits, because of how this
 * hook is wired up in ClientDashboard.
 *
 * ClientDashboard's `role` is plain useState(null) in App.js with the
 * comment "Always start at login — role is session-only, never persisted
 * across page loads." That means ClientDashboard ONLY ever mounts in the
 * instant right after a successful login — there is no scenario where it
 * mounts from a stale, already-expired session. So whenever a user closed
 * the tab or shut down the laptop last time without clicking "Sign out"
 * (leaving an old `vatica_last_activity` stamp behind in localStorage),
 * the old mount check saw that leftover stamp, decided the BRAND NEW
 * session was "expired," and logged the user straight back out one tick
 * after they'd just logged in — forcing a second login. That was the
 * "expired token" bug.
 *
 * The actual closed-tab/shut-down-laptop requirement — the user should
 * always have to log in again — is already fully satisfied by App.js's
 * design above: every reload shows the Login screen, full stop, regardless
 * of any token or timestamp sitting in localStorage. This hook's only job
 * now is the LIVE 20-minute idle timeout described above.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * USAGE — call once at the top of ClientDashboard (or any auth-guarded page)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   import { useInactivityLogout, clearActivityTimestamp } from './useInactivityLogout';
 *
 *   // Inside ClientDashboard:
 *   useInactivityLogout(onLogout);
 *
 *   // Inside the logout button handler:
 *   clearActivityTimestamp();   // remove the stamp so the next fresh login
 *   onLogout();                 // gets a clean 20-minute window
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW THE TIMER WORKS
 * ─────────────────────────────────────────────────────────────────────────────
 *  • Mount:         start a fresh 20-minute window unconditionally — every
 *                   mount represents a brand-new login (see above).
 *  • Active:        any of {mousedown, keydown, touchstart, click} resets both
 *                   the in-memory setTimeout AND the stored timestamp.
 *  • localStorage:  written at most once per 30 seconds (throttled) to avoid
 *                   hammering storage on every mouse move.
 *  • Unmount:       timeout is cleared and event listeners are removed.
 */

import { useEffect, useRef, useCallback } from 'react';

// ── Constants ─────────────────────────────────────────────────────────────────

const TIMEOUT_MS       = 20 * 60 * 1000;  // 20 minutes
const WRITE_THROTTLE   = 30 * 1000;        // write localStorage at most every 30 s
export const ACTIVITY_KEY = 'vatica_last_activity';

// ── Public utility — call from the logout handler ─────────────────────────────

/**
 * Remove the activity stamp so the next login starts a fresh 20-min window.
 * Call this whenever the user explicitly signs out.
 */
export function clearActivityTimestamp() {
  localStorage.removeItem(ACTIVITY_KEY);
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param {Function} onLogout   Called when 20 minutes pass with no
 *                              mousedown/keydown/touchstart/click activity.
 */
export function useInactivityLogout(onLogout) {
  // Use a ref so the callback is always current without being a dep of anything.
  const logoutRef      = useRef(onLogout);
  const timerRef       = useRef(null);
  const lastWriteRef   = useRef(0);     // last time we wrote to localStorage

  useEffect(() => { logoutRef.current = onLogout; }, [onLogout]);

  // resetTimer is stable: it captures only refs, not stateful values.
  const resetTimer = useCallback(() => {
    // ── 1. Reset the in-memory countdown ──────────────────────────────────
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      clearActivityTimestamp();
      logoutRef.current?.();
    }, TIMEOUT_MS);

    // ── 2. Throttle-write the wall-clock timestamp ─────────────────────────
    const now = Date.now();
    if (now - lastWriteRef.current >= WRITE_THROTTLE) {
      lastWriteRef.current = now;
      try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch (_) { /* storage full */ }
    }
  }, []);

  useEffect(() => {
    // Every mount of this hook is a brand-new login (see header comment) —
    // start a clean 20-minute window unconditionally rather than comparing
    // against whatever timestamp happens to be sitting in localStorage from
    // a previous, possibly-unrelated session.
    lastWriteRef.current = Date.now();
    try { localStorage.setItem(ACTIVITY_KEY, String(lastWriteRef.current)); } catch (_) { /**/ }

    resetTimer();   // arm the 20-minute in-memory timer

    const EVENTS = ['mousedown', 'keydown', 'touchstart', 'click'];
    EVENTS.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      EVENTS.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, [resetTimer]);
}
